import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "components/common/Button";
import { Callout } from "components/common/Callout";
import { plural } from "components/common/ImpactRow";
import { formatShortDate } from "@/lib/dates";
import {
  attachSubscription,
  type UnattachedSubscription,
} from "@/lib/api/organizations";
import { billingKeys } from "@/lib/api/billing/keys";
import { useUnattachedSubscriptions } from "@/lib/api/billing/org-billing";

export type AttachResult = NonNullable<
  Awaited<ReturnType<typeof attachSubscription>>
>;

const NONE: UnattachedSubscription[] = [];

/// `company` is the legacy stored spelling of a non-personal org.
const NON_PERSONAL_KIND = "company";
const kindLabel = (kind: string) =>
  kind === NON_PERSONAL_KIND ? "organization" : kind;

/// Mounted only for an owner of an org with no subscription, so leaving that
/// state drops the list and coming back reads it afresh.
export const UnattachedSubscriptions: React.FC<{
  orgId: string;
  memberCount: number;
  onAttached: (result: AttachResult) => void;
  onError: (message: string | null) => void;
}> = ({ orgId, memberCount, onAttached, onError }) => {
  const navigate = useNavigate();
  const client = useQueryClient();
  const unattached = useUnattachedSubscriptions(orgId).data ?? NONE;
  const [attaching, setAttaching] = useState<string | null>(null);

  const attach = useCallback(
    async (subscriptionId: string) => {
      setAttaching(subscriptionId);
      onError(null);
      const result = await attachSubscription(orgId, subscriptionId);
      setAttaching(null);
      if (!result) {
        onError("The subscription could not be attached. Please try again.");
        return;
      }
      client.setQueryData(billingKeys.unattached(orgId), NONE);
      onAttached(result);
    },
    [client, orgId, onAttached, onError],
  );

  if (unattached.length === 0) return null;

  // Kind is the one attach rule an owner cannot override, so with nothing
  // attachable the remedy is another organization, not this page.
  const anyAttachable = unattached.some((row) => row.attachable);

  return (
    <Callout
      variant="warning"
      title="A subscription billed to your address isn't attached to any organization"
      className="mx-2"
    >
      <div
        className="flex flex-col gap-2"
        data-testid="unattached-subscriptions"
      >
        <span>
          {anyAttachable
            ? "Attach the one you bought: whoever pays for it then pays for this organization, and if their card fails, it becomes read-only. Contact support about any you don't recognise."
            : `${unattached.length === 1 ? "It" : "Each"} can only be attached to an organization of the kind it was bought for.`}
        </span>
        {unattached.map((row) => (
          <span
            key={row.subscription_id}
            className="flex items-start justify-between gap-3"
          >
            <span className="min-w-0 flex-1 text-fg-medium">
              {[
                row.plan_name ?? "Subscription",
                `${row.seats} ${plural(row.seats, "seat")}`,
                row.org_name
                  ? `bought for “${row.org_name}”${row.org_kind ? ` (${kindLabel(row.org_kind)})` : ""}`
                  : row.org_kind
                    ? row.org_kind === NON_PERSONAL_KIND
                      ? "bought for an organization"
                      : `bought for a ${row.org_kind} organization`
                    : null,
                row.since ? `since ${formatShortDate(row.since * 1000)}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              {row.attachable && row.seats < memberCount && (
                <span
                  className="mt-0.5 block text-warning"
                  data-testid="attach-seat-gap"
                >
                  {`${row.seats} ${plural(row.seats, "seat")} for ${memberCount} members — ` +
                    `add seats after attaching to invite anyone new.`}
                </span>
              )}
            </span>
            <span className="shrink-0">
              {row.attachable ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={attaching === row.subscription_id}
                  disabled={attaching !== null}
                  onClick={() => void attach(row.subscription_id)}
                  data-testid="attach-subscription"
                >
                  Attach to this organization
                </Button>
              ) : row.org_kind === NON_PERSONAL_KIND ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    // Intent, never proof: the attach door re-checks every rule.
                    navigate("/organizations/new", {
                      state: {
                        attachSubscription: {
                          subscription_id: row.subscription_id,
                          plan_name: row.plan_name,
                          seats: row.seats,
                        },
                      },
                    })
                  }
                  data-testid="create-org-for-subscription"
                >
                  Create an organization
                </Button>
              ) : (
                <span className="whitespace-nowrap text-fg-muted-regular-small">
                  Attach it from your personal organization
                </span>
              )}
            </span>
          </span>
        ))}
      </div>
    </Callout>
  );
};
